import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { Play, X, MapPin, Calendar } from "lucide-react";
import { JsonLd } from "@/components/JsonLd";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const YOUTUBE_RE =
  /(?:youtube\.com\/(?:watch\?(?:[^#]*&)?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/i;
const VIMEO_RE = /vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/)?(\d{6,})/i;

const youTubeId = (url) => {
  const m = typeof url === "string" ? url.match(YOUTUBE_RE) : null;
  return m ? m[1] : null;
};

const vimeoId = (url) => {
  const m = typeof url === "string" ? url.match(VIMEO_RE) : null;
  return m ? m[1] : null;
};

// Returns an autoplaying embed URL for YouTube/Vimeo, or null for direct files.
const embedUrl = (url) => {
  const yt = youTubeId(url);
  if (yt) return `https://www.youtube.com/embed/${yt}?autoplay=1&rel=0&playsinline=1`;
  const vm = vimeoId(url);
  if (vm) return `https://player.vimeo.com/video/${vm}?autoplay=1`;
  return null;
};

// Poster falls back to YouTube's own still when the admin didn't set one.
const posterFor = (v) => {
  if (v.poster_url) return v.poster_url;
  const yt = youTubeId(v.video_url);
  return yt ? `https://i.ytimg.com/vi/${yt}/hqdefault.jpg` : "";
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const Videos = () => {
  const settings = useSiteSettings();
  const [active, setActive] = useState(null);

  const { data: videos = [] } = useQuery({
    queryKey: ["videos"],
    queryFn: async () => (await axios.get(`${API_BASE}/videos`)).data ?? [],
  });

  const close = useCallback(() => setActive(null), []);

  // Escape closes the lightbox
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, close]);

  // Lock body scroll while the lightbox is open, restore it on close
  useEffect(() => {
    if (!active) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [active]);

  // Admin can hide the videos section globally
  if (settings.videos_visible === false) return null;
  // No placeholder fallback — an empty Videos section simply doesn't render
  if (videos.length === 0) return null;

  const schemaItems = videos
    .map((v) => {
      const thumb = posterFor(v);
      if (!v.title || !thumb || !v.video_url) return null;
      const embed = embedUrl(v.video_url);
      return {
        "@type": "VideoObject",
        name: v.title,
        description: v.description || v.title,
        thumbnailUrl: thumb,
        ...(v.show_date ? { uploadDate: v.show_date } : {}),
        ...(embed ? { embedUrl: embed } : { contentUrl: v.video_url }),
      };
    })
    .filter(Boolean);

  const videoSchema =
    schemaItems.length > 0
      ? { "@context": "https://schema.org", "@graph": schemaItems }
      : null;

  const activeEmbed = active ? embedUrl(active.video_url) : null;

  return (
    <>
      {videoSchema && <JsonLd id="videos" data={videoSchema} />}
      <section
        id="videos"
        data-testid="videos-section"
        className="relative bg-[#0A0A0A] py-24 md:py-32 px-6 md:px-16 border-t border-white/5"
      >
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex items-end justify-between flex-wrap gap-6 mb-12"
          >
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-10 bg-[#DC2626]" />
                <span className="font-oswald text-xs uppercase tracking-[0.3em] text-[#DC2626]">
                  Live Footage
                </span>
              </div>
              <h2 className="font-anton uppercase text-white text-5xl md:text-6xl lg:text-7xl leading-[0.9]">
                From The <span className="text-[#B91C1C]">Stage</span>
              </h2>
            </div>
            <p className="max-w-sm text-white/60 font-manrope">
              Straight off the board and out of the crowd — clips from recent shows.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {videos.map((v, i) => {
              const poster = posterFor(v);
              const isEmbed = Boolean(embedUrl(v.video_url));
              const dateLabel = formatDate(v.show_date);
              return (
                <motion.div
                  key={v.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: (i % 6) * 0.08 }}
                >
                  <button
                    type="button"
                    onClick={() => setActive(v)}
                    data-testid={`video-card-${v.id}`}
                    aria-label={`Play video${v.title ? `: ${v.title}` : ""}`}
                    className="group relative block w-full text-left aspect-video overflow-hidden bg-black grain-overlay border border-white/10 hover:border-[#B91C1C] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#DC2626]"
                  >
                    {poster ? (
                      <img
                        src={poster}
                        alt={v.title || ""}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : isEmbed ? (
                      <div className="absolute inset-0 bg-[#0F0808]" />
                    ) : (
                      <video
                        src={v.video_url}
                        preload="metadata"
                        muted
                        playsInline
                        tabIndex={-1}
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover pointer-events-none transition-transform duration-700 group-hover:scale-105"
                      />
                    )}

                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

                    <span
                      aria-hidden="true"
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <span className="flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-[#B91C1C] text-white brutal-shadow group-hover:bg-[#991B1B] transition-colors">
                        <Play size={24} className="translate-x-[1px]" fill="currentColor" />
                      </span>
                    </span>

                    <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                      <div className="font-oswald uppercase tracking-wide text-white text-base md:text-lg leading-tight truncate">
                        {v.title || `Video ${i + 1}`}
                      </div>
                      {(v.venue || dateLabel) && (
                        <div className="flex items-center gap-4 mt-1.5 text-white/60 font-manrope text-xs">
                          {v.venue && (
                            <span className="flex items-center gap-1.5 truncate">
                              <MapPin size={12} className="shrink-0" />
                              <span className="truncate">{v.venue}</span>
                            </span>
                          )}
                          {dateLabel && (
                            <span className="flex items-center gap-1.5 whitespace-nowrap">
                              <Calendar size={12} className="shrink-0" />
                              {dateLabel}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            key="video-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            role="dialog"
            aria-modal="true"
            aria-label={active.title || "Video player"}
            data-testid="video-lightbox"
            onClick={close}
            className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          >
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="video-lightbox-panel"
              className="relative w-full max-w-5xl"
            >
              <button
                type="button"
                onClick={close}
                aria-label="Close video"
                data-testid="video-lightbox-close"
                className="absolute -top-11 right-0 flex items-center justify-center w-9 h-9 bg-[#B91C1C] hover:bg-[#991B1B] text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <X size={18} />
              </button>

              <div className="relative aspect-video bg-black border border-white/10">
                {activeEmbed ? (
                  <iframe
                    key={active.id}
                    src={activeEmbed}
                    title={active.title || "Video"}
                    data-testid="video-lightbox-iframe"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full border-0"
                  />
                ) : (
                  <video
                    key={active.id}
                    src={active.video_url}
                    poster={active.poster_url || undefined}
                    controls
                    autoPlay
                    playsInline
                    preload="metadata"
                    data-testid="video-lightbox-player"
                    className="absolute inset-0 w-full h-full bg-black"
                  />
                )}
              </div>

              <div className="mt-4">
                <div className="font-anton uppercase text-white text-2xl md:text-3xl leading-tight">
                  {active.title || "Untitled"}
                </div>
                {(active.venue || formatDate(active.show_date)) && (
                  <div className="font-oswald uppercase tracking-[0.2em] text-xs text-[#DC2626] mt-2">
                    {[active.venue, formatDate(active.show_date)].filter(Boolean).join(" · ")}
                  </div>
                )}
                {active.description && (
                  <p className="font-manrope text-white/60 text-sm mt-3 max-w-2xl">
                    {active.description}
                  </p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
